import { useCallback, useEffect, useState } from 'react';
import { Grid, TableCell, TableRow } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { CustomDictionarySuggestedWord } from 'store/queries/dictionaries/types';
import { SuggestedWordStatus } from '@verify/shared-utils';
import { makeStyles } from 'tss-react/mui';
import { useCustomDictionaries } from 'store/queries/dictionaries/customDictionaries';
import { useSuggestedWords } from 'store/queries/dictionaries/suggestedWords';
import GVIconButton from 'components/lib/GVIconButton/GVIconButton';
import { useQuery } from '@redux-requests/react';
import { fetchActiveUserOrganization } from 'store/myAccount/requests';
import GVTypography from 'components/lib/GVTypography/GVTypography';
import GVUserAvatar, { GVUserAvatarSizes } from 'components/GVUserAvatar/GVUserAvatar';
import { MultiSelect } from '@globalvisioninc/ui/multi-select';
import classNames from 'classnames';
import { debounce } from 'lodash';
import styles from './SuggestedWordRow.module.css';

interface SuggestedWordRowProps {
  suggestedWord: CustomDictionarySuggestedWord;
}

const EMPTY_DICTIONARY_LABEL = '-- Select a Dictionary --';

const useStyles = makeStyles()(() => ({
  avatar: {
    marginTop: '2px',
    marginBottom: '2px',
    marginRight: '8px',
    width: '28px !important',
    height: '28px !important',
  },
  actionIcon: {
    color: '#fff',
    paddingLeft: '5px !important',
    paddingRight: '5px !important',
    '&:hover': { background: 'rgba(255, 255, 255, 0.12)' },
  },
  tableRow: {
    ' td': {
      paddingTop: 0,
      paddingBottom: 0,
      height: '34px',
    },
  },
}));

const SuggestedWordRow = ({ suggestedWord }: SuggestedWordRowProps) => {
  const { classes } = useStyles();
  const [error, setError] = useState(false);
  const { customDictionaryList, isLoading } = useCustomDictionaries({ getAll: true });
  const { updateSuggestedWordStatusMutation } = useSuggestedWords();

  const { data: organization } = useQuery({ type: fetchActiveUserOrganization });
  const ssoEnabled = organization?.ssoEnabled;

  const members = organization?.members || [];
  const member = members.find((member) => {
    return member.id === suggestedWord.requestorId;
  });

  const dictionaryOptions = customDictionaryList.map((dictionary) => ({
    label: dictionary.name,
    value: dictionary.id,
  }));

  const userIdentifier = ssoEnabled ? member?.email : member?.name;

  const [assignedDictionaryIds, setAssignedDictionaryIds] = useState<string[]>(
    suggestedWord.suggestedDictionaryId ? [suggestedWord.suggestedDictionaryId] : [],
  );

  useEffect(() => {
    // remove any assigned dictionaries that were deleted
    if (!isLoading) {
      setAssignedDictionaryIds(
        assignedDictionaryIds.filter((id) => customDictionaryList.some((dictionary) => dictionary.id === id)),
      );
    }
  }, [customDictionaryList]);

  const handleAssignedDictionaryChange = (nextIds: string[]) => {
    if (error && nextIds.length > 0) {
      setError(false);
    }

    setAssignedDictionaryIds(nextIds);
  };

  const handleAcceptWord = () => {
    if (assignedDictionaryIds.length === 0) {
      setError(true);
      return;
    }

    updateSuggestedWordStatusMutation.mutate({
      id: suggestedWord.id,
      status: SuggestedWordStatus.APPROVED,
      assignedDictionaryIds: assignedDictionaryIds,
    });
  };

  const handleRejectWord = () => {
    updateSuggestedWordStatusMutation.mutate({
      id: suggestedWord.id,
      assignedDictionaryIds: assignedDictionaryIds,
      status: SuggestedWordStatus.REJECTED,
    });
  };
  const debouncedRejectWord = useCallback(debounce(handleRejectWord, 300), []);

  return (
    <>
      <TableRow className={classes.tableRow} key={`enhanced-table-checkbox-${suggestedWord.id}`} tabIndex={-1}>
        <TableCell size="medium" align="left">
          <GVTypography emphasis="superhigh" variant="subtitle1">
            {suggestedWord.suggestedWord}
          </GVTypography>
        </TableCell>
        <TableCell align="left">
          <MultiSelect.Root
            className={styles.panel}
            options={dictionaryOptions}
            value={assignedDictionaryIds}
            onValueChange={handleAssignedDictionaryChange}
            label={EMPTY_DICTIONARY_LABEL}
            isLabelHidden
            hasSearch={false}
            hasBadge={false}
            badgeSeparator=","
            maxCount={assignedDictionaryIds.length || 1}
            shouldWrapValues={false}
          >
            <MultiSelect.Trigger
              className={classNames(styles.assignDictionary, {
                [styles.assignDictionaryError]: error,
              })}
            />
          </MultiSelect.Root>
        </TableCell>
        <TableCell align="left">
          {member && (
            <Grid container alignItems={'center'} flexDirection={'row'} width="100%">
              <GVUserAvatar
                className={classes.avatar}
                size={GVUserAvatarSizes.SMALL}
                user={{ name: userIdentifier, picture: member.picture }}
              />
              <GVTypography emphasis="superhigh" variant="subtitle1">
                {userIdentifier}
              </GVTypography>
            </Grid>
          )}
        </TableCell>
        <TableCell align="right">
          <GVIconButton
            className={classes.actionIcon}
            size="small"
            onClick={() => {
              debouncedRejectWord();
            }}
            icon={<CloseIcon />}
          />
          <GVIconButton className={classes.actionIcon} size="small" onClick={handleAcceptWord} icon={<CheckIcon />} />
        </TableCell>
      </TableRow>
    </>
  );
};

export default SuggestedWordRow;
